import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  Grid,
  Stack,
  Paper,
  Button,
  Chip,
  Divider,
} from "@mui/material";
import { ChevronLeft, ClipboardList, Activity, Calendar, Droplets, FlaskConical } from "lucide-react";
import { kioskModel } from "../Axios/AxiosConfig";

const QuestionAnswer = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quizData, setQuizData] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [selectedSessionIndex, setSelectedSessionIndex] = useState(0);
  const [followupData, setFollowupData] = useState([]);

  useEffect(() => {
    const fetchDiabetesData = async () => {
      if (!uid) return;
      try {
        setLoading(true);

        const [answersRes, questionsRes, followupRes] = await Promise.all([
          kioskModel
            .get(`api/v1/clinical/diabetes-assessment/answers/${uid}`)
            .catch((err) => {
              console.error("Error fetching diabetes answers:", err);
              return null;
            }),
          kioskModel
            .get(`api/v1/diabetes-assessment/questions`)
            .catch((err) => {
              console.error("Error fetching diabetes questions:", err);
              return null;
            }),
          kioskModel
            .get(`api/v1/clinical/diabetes-assessment/followup/${uid}`)
            .catch((err) => {
              console.error("Error fetching diabetes followup:", err);
              return null;
            }),
        ]);

        if (answersRes && answersRes.status === 200) {
          const answersList =
            answersRes.data?.data || answersRes.data || [];
          const sortedAnswers = Array.isArray(answersList)
            ? answersList.sort(
                (a, b) =>
                  new Date(b.timestamp || b.createdAt) -
                  new Date(a.timestamp || a.createdAt)
              )
            : [];
          setQuizData(sortedAnswers);
        } else {
          setQuizData([]);
        }

        if (questionsRes && questionsRes.status === 200) {
          const questionsList =
            questionsRes.data?.data || questionsRes.data || [];
          setQuestions(Array.isArray(questionsList) ? questionsList : []);
        } else {
          setQuestions([]);
        }
        if (followupRes && followupRes.status === 200) {
          const followupList =
            followupRes.data?.data || followupRes.data || [];
          const sortedFollowup = Array.isArray(followupList)
            ? followupList.sort(
                (a, b) =>
                  new Date(b.timestamp || b.createdAt) -
                  new Date(a.timestamp || a.createdAt)
              )
            : [];
          setFollowupData(sortedFollowup);
        } else {
          setFollowupData([]);
        }
        setSelectedSessionIndex(0);
      } catch (error) {
        console.error("Error loading diabetes data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDiabetesData();
  }, [uid]);

  const currentSession = quizData[selectedSessionIndex];

  const currentSessionQA = useMemo(() => {
    if (!questions.length) return [];

    // Build a map of questionId -> answer from the current session
    const answersMap = {};
    if (currentSession?.answers) {
      currentSession.answers.forEach((ans) => {
        answersMap[ans.questionId] = ans.answer;
      });
    }

    // Always show ALL questions, with answers mapped if available
    return questions.map((q) => ({
      questionId: q.questionId,
      questionText: q.questionText || q.questionId,
      section: q.section || "Assessment",
      answer:
        answersMap[q.questionId] !== undefined
          ? answersMap[q.questionId]
          : null,
      type: q.type,
    }));
  }, [currentSession, questions]);

  const formatAnswer = (answer) => {
    if (answer === null || answer === undefined) return "---";
    if (typeof answer === "boolean") return answer ? "Yes" : "No";
    if (Array.isArray(answer)) return answer.join(", ");
    if (typeof answer === "object") return JSON.stringify(answer);
    return String(answer);
  };

  if (loading) {
    return (
      <Box className="flex flex-col items-center justify-center h-screen bg-[#FDFDFD]">
        <CircularProgress sx={{ color: "#DA6E0B" }} />
        <Typography className="mt-4 text-[#918165] font-medium animate-pulse">
          Loading Assessment Records...
        </Typography>
      </Box>
    );
  }

  if (currentSessionQA.length === 0) {
    return (
      <Box className="flex flex-col items-center justify-center h-screen p-8 text-center bg-[#FDFDFD]">
        <ClipboardList className="w-16 h-16 text-gray-300 mb-4" />
        <Typography
          variant="h5"
          className="text-[#3B362A] font-bold mb-2"
        >
          No Assessment Questions Available
        </Typography>
        <Typography className="text-[#918165] mb-6">
          Could not load diabetes assessment questions.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ChevronLeft />}
          onClick={() => navigate(-1)}
          sx={{
            borderColor: "#DA6E0B",
            color: "#DA6E0B",
            borderRadius: "10px",
            textTransform: "none",
          }}
        >
          Back to Profile
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#FDFDFD", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          background: "#fff8f0",
          px: 3,
          py: 2,
          borderBottom: "1px solid #f0e6d9",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Button
          startIcon={<ChevronLeft size={20} />}
          onClick={() => navigate(-1)}
          sx={{
            color: "#DA6E0B",
            textTransform: "none",
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
            minWidth: "auto",
          }}
        >
          Back
        </Button>
        <div className="flex items-center gap-2">
          <ClipboardList size={22} className="text-[#DA6E0B]" />
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              fontFamily: "'Poppins', sans-serif",
              color: "var(--themePrimaryColor, #DA6E0B)",
            }}
          >
            Diabetes Assessment Q&A
          </Typography>
        </div>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Timeline sidebar if there are multiple submissions */}
          {quizData.length > 1 && (
            <Grid item xs={12} md={3} sx={{ borderRight: "1px solid #e2e8f0", pr: 2 }}>
              <Typography
                variant="overline"
                sx={{
                  color: "#918165",
                  fontWeight: "bold",
                  tracking: "0.1em",
                  mb: 2,
                  display: "block",
                }}
              >
                QUIZ HISTORY
              </Typography>
              <Stack spacing={1.5}>
                {quizData.map((session, index) => (
                  <Paper
                    key={session.id || index}
                    onClick={() => setSelectedSessionIndex(index)}
                    elevation={0}
                    sx={{
                      p: 2,
                      cursor: "pointer",
                      borderRadius: "10px",
                      border: "1px solid",
                      borderColor:
                        selectedSessionIndex === index
                          ? "var(--themePrimaryColor, #DA6E0B)"
                          : "#e2e8f0",
                      backgroundColor:
                        selectedSessionIndex === index
                          ? "#fdf8f4"
                          : "transparent",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "var(--themePrimaryColor, #DA6E0B)",
                        backgroundColor: "#fdf8f4",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color:
                          selectedSessionIndex === index
                            ? "var(--themePrimaryColor, #DA6E0B)"
                            : "#3b362a",
                      }}
                    >
                      {new Date(
                        session.timestamp || session.createdAt
                      ).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "11px", color: "#64748b", mt: 0.5 }}
                    >
                      {new Date(
                        session.timestamp || session.createdAt
                      ).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Grid>
          )}

          {/* Main answers content */}
          <Grid item xs={12} md={quizData.length > 1 ? 9 : 12}>

            {/* Diabetes Follow-up Section - Shown at top */}
            {followupData.length > 0 && followupData[0]?.isDiabetic && (
              <Box sx={{ mb: 3 }}>
                <div className="flex items-center gap-2 mb-3">
                  <Activity size={20} className="text-[#DA6E0B]" />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      fontFamily: "'Poppins', sans-serif",
                      color: "var(--themePrimaryColor, #DA6E0B)",
                      fontSize: "16px",
                    }}
                  >
                    Diabetes Records
                  </Typography>
                  <Chip
                    label={`${followupData.length} ${followupData.length === 1 ? 'record' : 'records'}`}
                    size="small"
                    sx={{
                      backgroundColor: "#fff7ed",
                      color: "#DA6E0B",
                      fontWeight: 600,
                      fontSize: "11px",
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  />
                </div>

                {/* Latest Record - Highlighted */}
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "14px",
                    border: "2px solid var(--themePrimaryColor, #DA6E0B)",
                    backgroundColor: "#fffbf7",
                    mb: 2,
                    position: "relative",
                    overflow: "visible",
                  }}
                >
                  <Chip
                    label="Latest"
                    size="small"
                    sx={{
                      position: "absolute",
                      top: -10,
                      left: 16,
                      backgroundColor: "var(--themePrimaryColor, #DA6E0B)",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "11px",
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Calendar size={14} className="text-[#64748b]" />
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#64748b",
                        fontFamily: "'Poppins', sans-serif",
                      }}
                    >
                      {new Date(followupData[0].timestamp || followupData[0].createdAt).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 1.5,
                          borderRadius: "10px",
                          backgroundColor: "#fff",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 0.5 }}>
                          <Droplets size={14} className="text-[#3b82f6]" />
                          <Typography sx={{ fontSize: "11px", color: "#64748b", fontFamily: "'Poppins', sans-serif", fontWeight: 500 }}>
                            Fasting Glucose
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#1e293b", fontFamily: "'Poppins', sans-serif" }}>
                          {followupData[0].fastingGlucose ?? "---"}
                        </Typography>
                        <Typography sx={{ fontSize: "10px", color: "#94a3b8", fontFamily: "'Poppins', sans-serif" }}>mg/dL</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 1.5,
                          borderRadius: "10px",
                          backgroundColor: "#fff",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 0.5 }}>
                          <FlaskConical size={14} className="text-[#8b5cf6]" />
                          <Typography sx={{ fontSize: "11px", color: "#64748b", fontFamily: "'Poppins', sans-serif", fontWeight: 500 }}>
                            HbA1c
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#1e293b", fontFamily: "'Poppins', sans-serif" }}>
                          {followupData[0].hba1c ?? "---"}
                        </Typography>
                        <Typography sx={{ fontSize: "10px", color: "#94a3b8", fontFamily: "'Poppins', sans-serif" }}>%</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 1.5,
                          borderRadius: "10px",
                          backgroundColor: "#fff",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <Typography sx={{ fontSize: "11px", color: "#64748b", fontFamily: "'Poppins', sans-serif", fontWeight: 500, mb: 0.5 }}>
                          Diabetes Type
                        </Typography>
                        <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#1e293b", fontFamily: "'Poppins', sans-serif" }}>
                          {followupData[0].diabetesType || "---"}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 1.5,
                          borderRadius: "10px",
                          backgroundColor: "#fff",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <Typography sx={{ fontSize: "11px", color: "#64748b", fontFamily: "'Poppins', sans-serif", fontWeight: 500, mb: 0.5 }}>
                          Is Diabetic
                        </Typography>
                        <Chip
                          label={followupData[0].isDiabetic ? "Yes" : "No"}
                          size="small"
                          sx={{
                            mt: 0.5,
                            fontWeight: 700,
                            fontFamily: "'Poppins', sans-serif",
                            backgroundColor: followupData[0].isDiabetic ? "#fef2f2" : "#f0fdf4",
                            color: followupData[0].isDiabetic ? "#dc2626" : "#16a34a",
                            border: followupData[0].isDiabetic ? "1px solid #fecaca" : "1px solid #bbf7d0",
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Card>

                {/* Older Records */}
                {followupData.length > 1 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#64748b",
                        fontFamily: "'Poppins', sans-serif",
                        mb: 1.5,
                      }}
                    >
                      Previous Records
                    </Typography>
                    <Stack spacing={1.5}>
                      {followupData.slice(1).map((record, idx) => (
                        <Card
                          key={record.id || idx}
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: "10px",
                            border: "1px solid #e2e8f0",
                            backgroundColor: "#fafaf9",
                            "&:hover": {
                              boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Calendar size={13} className="text-[#94a3b8]" />
                              <Typography sx={{ fontSize: "12px", color: "#64748b", fontFamily: "'Poppins', sans-serif" }}>
                                {new Date(record.timestamp || record.createdAt).toLocaleString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                              <Typography sx={{ fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}>
                                <span style={{ color: "#94a3b8", fontWeight: 500 }}>Fasting Glucose: </span>
                                <span style={{ color: "#1e293b", fontWeight: 600 }}>{record.fastingGlucose ?? "---"}</span>
                              </Typography>
                              <Typography sx={{ fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}>
                                <span style={{ color: "#94a3b8", fontWeight: 500 }}>HbA1c: </span>
                                <span style={{ color: "#1e293b", fontWeight: 600 }}>{record.hba1c ?? "---"}</span>
                              </Typography>
                              <Typography sx={{ fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}>
                                <span style={{ color: "#94a3b8", fontWeight: 500 }}>Type: </span>
                                <span style={{ color: "#1e293b", fontWeight: 600 }}>{record.diabetesType || "---"}</span>
                              </Typography>
                              <Chip
                                label={record.isDiabetic ? "Diabetic" : "Non-Diabetic"}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "11px",
                                  fontFamily: "'Poppins', sans-serif",
                                  backgroundColor: record.isDiabetic ? "#fef2f2" : "#f0fdf4",
                                  color: record.isDiabetic ? "#dc2626" : "#16a34a",
                                  border: record.isDiabetic ? "1px solid #fecaca" : "1px solid #bbf7d0",
                                }}
                              />
                            </Box>
                          </Box>
                        </Card>
                      ))}
                    </Stack>
                  </Box>
                )}

                <Divider sx={{ mt: 3 }} />
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              {currentSession?.timestamp || currentSession?.createdAt ? (
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#64748b",
                    mb: 1,
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  Assessment Date:{" "}
                  {new Date(
                    currentSession?.timestamp || currentSession?.createdAt
                  ).toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              ) : (
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#64748b",
                    mb: 1,
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  This patient has not submitted answers yet.
                </Typography>
              )}
            </Box>

            <Stack spacing={2}>
              {currentSessionQA.map((qa, index) => (
                <Card
                  key={qa.questionId || index}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    backgroundColor: "#fafaf9",
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#2c251e",
                      mb: 1,
                      fontSize: "14px",
                      fontFamily: "'Poppins', sans-serif",
                      lineHeight: 1.4,
                    }}
                  >
                    {index + 1}. {qa.questionText}
                  </Typography>

                  <Typography
                    sx={{
                      color: qa.answer !== null ? "#DA6E0B" : "#94a3b8",
                      fontSize: "14px",
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 500,
                      pl: 2.5,
                    }}
                  >
                    {formatAnswer(qa.answer)}
                  </Typography>
                </Card>
              ))}
            </Stack>

          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default QuestionAnswer;
